import SupportService from '@apis/SupportService';
import LndButton from '@components/LndButton';
import type {
  RequestFormProps,
  RequestFormValues,
  SubmitRequestPayload,
} from '@customTypes/support/supportRequest';
import { MODAL_ACTION } from '@enums/common';
import { REQUEST_TYPE } from '@enums/Support';
import { Form, Input, Modal } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import './styles.module.scss';

const { TextArea } = Input;

const FORM_PLACEHOLDERS = {
  FEATURE_TITLE: 'Enter a brief title for your feature request',
  BUG_TITLE: 'Enter a brief title describing the bug',
  FEATURE_DESCRIPTION:
    "Describe the feature you'd like to see. Include any specific use cases or problems it would solve.",
  BUG_DESCRIPTION:
    'Describe the bug in detail. Include steps to reproduce, expected behavior, and actual behavior.',
} as const;

const RequestForm: React.FC<RequestFormProps> = ({
  open,
  type = REQUEST_TYPE.FEATURE,
  onModalClosed,
}) => {
  const [form] = Form.useForm<RequestFormValues>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleModalClose = useCallback(
    (action: MODAL_ACTION): void => {
      form.resetFields();
      onModalClosed(action);
    },
    [form, onModalClosed],
  );

  const handleSubmit = useCallback(
    async (values: RequestFormValues): Promise<void> => {
      const payload: SubmitRequestPayload = {
        type,
        title: values.title,
        description: values.description,
      };

      try {
        setIsLoading(true);
        await SupportService.submitSupportRequest(payload);
        toast.success('Request submitted successfully');
        handleModalClose(MODAL_ACTION.Closed);
      } catch (error) {
        toast.error('Failed to submit request');
      } finally {
        setIsLoading(false);
      }
    },
    [type, handleModalClose],
  );

  const closeModal = useCallback((): void => {
    if (isLoading) return;
    handleModalClose(MODAL_ACTION.Cancel);
  }, [isLoading, handleModalClose]);

  const modalTitle = useMemo(
    () => (
      <div className="flex items-center">
        <span className="text-xl font-semibold">
          {type === REQUEST_TYPE.FEATURE ? 'Request a Feature' : 'Report a Bug'}
        </span>
      </div>
    ),
    [type],
  );

  const placeholders = useMemo(
    () => ({
      title:
        type === REQUEST_TYPE.FEATURE
          ? FORM_PLACEHOLDERS.FEATURE_TITLE
          : FORM_PLACEHOLDERS.BUG_TITLE,
      description:
        type === REQUEST_TYPE.FEATURE
          ? FORM_PLACEHOLDERS.FEATURE_DESCRIPTION
          : FORM_PLACEHOLDERS.BUG_DESCRIPTION,
    }),
    [type],
  );

  const submitButtonText = useMemo(
    () =>
      `Submit ${type === REQUEST_TYPE.FEATURE ? 'Feature Request' : 'Bug Report'}`,
    [type],
  );

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={closeModal}
      footer={null}
      destroyOnClose
      centered
      className="request-form-modal"
      width={600}
    >
      <Form<RequestFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="mt-6 space-y-6"
        initialValues={{ type }}
      >
        <Form.Item
          name="title"
          label="Title"
          rules={[
            { required: true, message: 'Please enter a title' },
            { max: 100, message: 'Title must be less than 100 characters' },
          ]}
          validateTrigger={['onChange', 'onBlur']}
        >
          <Input
            size="large"
            className="w-full pb-1"
            placeholder={placeholders.title}
          />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { required: true, message: 'Please provide a description' },
            { min: 10, message: 'Description must be at least 10 characters' },
          ]}
          validateTrigger={['onChange', 'onBlur']}
        >
          <TextArea
            size="large"
            className="w-full pb-1"
            rows={6}
            placeholder={placeholders.description}
          />
        </Form.Item>

        <Form.Item className="mb-0 flex justify-end">
          <LndButton size="large" onClick={closeModal} className="mr-2">
            Cancel
          </LndButton>
          <LndButton
            variant="primary"
            size="large"
            htmlType="submit"
            loading={isLoading}
          >
            {submitButtonText}
          </LndButton>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RequestForm;
